import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, Camera, FileText, ClipboardList, ArrowRight,
  Building2, Truck, HardHat, Home, MessageSquare, CheckCircle,
  AlertTriangle, Info, ChevronRight, Mail, BarChart2, Download,
  X, Loader, Lock, Eye, Users, Clock, Award, TrendingDown,
  ChevronDown, Zap
} from "lucide-react";

const WEBHOOK_URL = "https://fractional-hub.preview.emergentagent.com/api/webhooks/risksurvey";

// ── Demo Modal (webhook preserved exactly) ─────────────────────────────────
function DemoModal({ onClose }) {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", company: "", phone: "", message: "" });
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Unable to submit. Please email us at hello@risksurvey.ai");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="demo-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-navy px-6 py-5 flex items-start justify-between">
          <div>
            <h2 className="font-heading font-bold text-white text-lg">Schedule a Demo</h2>
            <p className="text-white/60 text-sm mt-0.5">We&apos;ll reach out within one business day.</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/50 hover:text-white transition-colors mt-0.5" data-testid="close-demo-modal">
            <X className="w-4 h-4" />
          </button>
        </div>
        {submitted ? (
          <div className="px-6 py-12 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="font-heading font-bold text-slate-800 text-lg mb-2">Request received!</h3>
            <p className="text-slate-500 text-sm mb-6">We&apos;ll be in touch within one business day to schedule your demo.</p>
            <button onClick={onClose} className="px-6 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Name *</label>
                <input required value={formData.name} onChange={set("name")} placeholder="Jane Smith"
                  data-testid="demo-name"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Work Email *</label>
                <input required type="email" value={formData.email} onChange={set("email")} placeholder="jane@insurer.com"
                  data-testid="demo-email"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
                <input value={formData.company} onChange={set("company")} placeholder="Acme Insurance"
                  data-testid="demo-company"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                <input type="tel" value={formData.phone} onChange={set("phone")} placeholder="555-0100"
                  data-testid="demo-phone"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tell us about your loss control needs</label>
              <textarea value={formData.message} onChange={set("message")} rows={3}
                placeholder="Lines of business, survey volume, current process..."
                data-testid="demo-message"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy resize-none" />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            <button type="submit" disabled={isSubmitting} data-testid="demo-submit"
              className="w-full flex items-center justify-center gap-2 h-11 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition-colors disabled:opacity-60">
              {isSubmitting ? <><Loader className="w-4 h-4 animate-spin" /> Submitting...</> : "Request Demo"}
            </button>
            <p className="text-center text-xs text-slate-400">No spam. Unsubscribe any time.</p>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Sample output data (unchanged) ─────────────────────────────────────────
const SAMPLE_SURVEYS = {
  commercial_property: {
    label: "Commercial Property",
    insured: "Acme Manufacturing LLC",
    address: "1234 Industrial Blvd, Dallas, TX",
    risk_score: 72,
    risk_level: "High",
    chat: [
      { role: "ai", text: "Thank you for completing this Commercial Property survey for Acme Manufacturing LLC at 1234 Industrial Blvd. I'll be walking you through a structured evaluation. Let's start with the building itself — can you tell me the year it was constructed, the primary construction type (wood frame, masonry, or steel), and the approximate square footage?" },
      { role: "user", text: "It was built in 1987. It's masonry construction — concrete block walls. About 45,000 square feet, single story." },
      { role: "ai", text: "Thank you. A 1987 masonry building of that size is a solid construction class. Now I'd like to look at your electrical systems. Could you tell me the age of the main electrical panel, and please upload a photo of the main electrical panel and any visible subpanels?\n\n---FINDING---\nSeverity: Medium\nConfidence: 71%\nCategory: Building Age\nIssue: 37-year-old building may have original plumbing and electrical systems requiring assessment.\nGuideline: Per industry loss control standards, buildings over 30 years old warrant detailed systems evaluation.\nRecommendation: Document last major systems upgrades (electrical, plumbing, HVAC) with supporting records.\n---END FINDING---" },
      { role: "user", text: "Here's a photo of the main panel." },
      { role: "ai", text: "I've reviewed the photo of your electrical panel. In the uploaded image, I can observe double-tapped breakers on circuit positions 4, 7, and 12 — meaning two conductors are connected to a single breaker terminal that is rated for only one conductor.\n\n---FINDING---\nSeverity: Critical\nConfidence: 94%\nCategory: Electrical Systems\nIssue: Double-tapped breakers identified on circuits 4, 7, and 12. This creates an overloaded circuit condition and elevated fire risk.\nGuideline: NFPA 70 (National Electrical Code) Article 408.41 prohibits more than one conductor per breaker terminal unless the equipment is specifically listed for that use.\nRecommendation: Engage a licensed electrician to correct double-tapped breakers immediately. Install additional breaker slots or use tandem breakers only where panel listing permits.\n---END FINDING---" },
    ],
    findings: [
      { sev: "Critical", title: "Double-Tapped Electrical Breakers", conf: 94, desc: "Double-tapped breakers identified on circuits 4, 7, and 12 in the main electrical panel.", rec: "Engage a licensed electrician to correct immediately.", ref: "NFPA 70, Article 408.41" },
      { sev: "Critical", title: "Sprinkler System — No Annual Inspection Record", conf: 91, desc: "Building is equipped with a wet pipe sprinkler system, but no documentation of annual inspection in the last 18 months was provided.", rec: "Schedule inspection by a licensed fire protection contractor within 30 days. Maintain records per NFPA 25.", ref: "NFPA 25, Section 5.2.1" },
      { sev: "High", title: "Roof Membrane Deterioration", conf: 85, desc: "Visible ponding water and membrane separation at the northeast corner. Roof was last replaced in 2004.", rec: "Engage a licensed roofing contractor for assessment and repair within 30 days to prevent water intrusion.", ref: "ISO Building Code standards; carrier underwriting guidelines §3.2" },
      { sev: "High", title: "Combustible Storage Adjacent to Electrical Equipment", conf: 88, desc: "Cardboard and packing materials stored within 3 feet of electrical subpanel in the production area.", rec: "Maintain a minimum 3-foot clear zone around all electrical equipment per NFPA 70-E.", ref: "NFPA 70-E, Section 205.4" },
      { sev: "Medium", title: "Fire Extinguisher Monthly Inspections Not Documented", conf: 78, desc: "Portable fire extinguishers in production and storage areas lack monthly inspection tags dated within the last 30 days.", rec: "Implement monthly visual inspection program with documented tags. Train designated safety staff.", ref: "NFPA 10, Section 7.2.2" },
      { sev: "Low", title: "Outdoor Storage Near Building Exterior", conf: 67, desc: "Pallets and raw materials stored within 5 feet of building exterior on south side.", rec: "Relocate to maintain minimum 10-foot clearance from building per loss control best practices.", ref: "Carrier Underwriting Guidelines §4.1.3" },
    ],
    risk_score_detail: { critical: 2, high: 2, medium: 1, low: 1 },
    project_plan: [
      { action: "Correct double-tapped breakers on circuits 4, 7, and 12", priority: "Critical", timeframe: "Immediately (within 7 days)" },
      { action: "Schedule annual sprinkler system inspection with licensed contractor", priority: "Critical", timeframe: "Within 30 days" },
      { action: "Obtain roofing contractor assessment and repair quote", priority: "High", timeframe: "Within 30 days" },
      { action: "Clear combustible materials from subpanel area — maintain 3-ft clearance", priority: "High", timeframe: "Within 14 days" },
      { action: "Implement monthly fire extinguisher inspection tags and documentation", priority: "Medium", timeframe: "Within 30 days" },
      { action: "Relocate outdoor pallet storage 10 ft from building exterior", priority: "Low", timeframe: "Within 90 days" },
    ],
  },
  workers_comp: {
    label: "Workers' Compensation",
    insured: "Premier Foods Distribution Co.",
    address: "800 Commerce Drive, Houston, TX",
    risk_score: 65,
    risk_level: "High",
    chat: [
      { role: "ai", text: "Welcome to the Workers' Compensation Loss Control Survey for Premier Foods Distribution Co. I'll guide you through a review of your workplace safety programs and physical conditions. To start, please describe your primary business operations — what activities do your employees perform, and how many employees are on site?" },
      { role: "user", text: "We're a food distribution warehouse. About 85 employees — forklift operators, order pickers, truck drivers, and office staff. We run two shifts, 6am to 10pm." },
      { role: "ai", text: "Thank you. A two-shift distribution operation with forklifts and truck drivers has several significant workers' comp exposures. Let's start with your forklift safety program. Are your forklift operators formally certified, and how recently was that training conducted? Could you also upload a photo of a typical forklift operating area?" },
    ],
    findings: [
      { sev: "Critical", title: "No Written Lockout/Tagout (LOTO) Program", conf: 92, desc: "No documented lockout/tagout program was found for conveyor and packaging line maintenance procedures.", rec: "Develop and implement a written LOTO program per OSHA requirements. Train all affected employees before next service activity.", ref: "OSHA 29 CFR 1910.147" },
      { sev: "High", title: "Forklift Operator Certification Records Incomplete", conf: 86, desc: "Of 12 forklift operators on site, only 7 have current certification documentation. 5 operators cannot produce training records.", rec: "Certify all operators within 30 days. Maintain operator training records for the life of employment.", ref: "OSHA 29 CFR 1910.178(l)" },
      { sev: "High", title: "SDS Not Accessible in Chemical Handling Area", conf: 83, desc: "Safety Data Sheets are stored in manager's office only. Not accessible to employees in chemical handling and cleaning supply area.", rec: "Place SDS binder or digital terminal in chemical handling area. Review HazCom training records.", ref: "OSHA HazCom 29 CFR 1910.1200(g)(8)" },
      { sev: "Medium", title: "No Documented Incident Investigation Procedure", conf: 74, desc: "Company reports no formal near-miss reporting or incident investigation process. Three recordable injuries in the past 12 months.", rec: "Implement written incident investigation program. Train supervisors on root cause analysis within 60 days.", ref: "OSHA 300 Log requirements; carrier loss control standard §2.4" },
      { sev: "Medium", title: "PPE Compliance — Eye Protection Not Consistently Worn", conf: 71, desc: "During walkthrough, 3 of 8 employees observed in chemical handling area were not wearing required splash goggles.", rec: "Reinforce PPE policy through training and supervisory enforcement. Post signage at area entry points.", ref: "OSHA 29 CFR 1910.133; carrier PPE policy" },
      { sev: "Low", title: "No Formal Return-to-Work Program", conf: 68, desc: "Carrier reports no documented light-duty or modified work program for injured employees.", rec: "Develop a return-to-work policy offering modified duty. Coordinate with occupational health provider.", ref: "WCIRB best practice guidelines" },
    ],
    risk_score_detail: { critical: 1, high: 2, medium: 2, low: 1 },
    project_plan: [
      { action: "Develop and implement written LOTO program — train all affected employees", priority: "Critical", timeframe: "Within 14 days" },
      { action: "Certify all 5 forklift operators lacking current documentation", priority: "High", timeframe: "Within 30 days" },
      { action: "Relocate SDS access point to chemical handling area", priority: "High", timeframe: "Within 7 days" },
      { action: "Implement formal near-miss and incident investigation program", priority: "Medium", timeframe: "Within 60 days" },
      { action: "Conduct PPE refresher training; enforce goggles policy", priority: "Medium", timeframe: "Within 30 days" },
      { action: "Draft light-duty return-to-work policy with HR", priority: "Low", timeframe: "Within 90 days" },
    ],
  },
  commercial_auto: {
    label: "Commercial Auto",
    insured: "Southwest Delivery Solutions LLC",
    address: "555 Fleet Drive, Phoenix, AZ",
    risk_score: 48,
    risk_level: "Moderate",
    chat: [
      { role: "ai", text: "Welcome to the Commercial Auto Loss Control Survey for Southwest Delivery Solutions LLC. I'll guide you through a review of your fleet operations, driver qualifications, and safety programs. Let's begin — how many vehicles are in your fleet, what types are they, and what is their primary use?" },
      { role: "user", text: "We have 22 vehicles — 15 box trucks (14-16 ft), 5 flatbed trucks, and 2 passenger vans. They're used for last-mile delivery and contractor equipment transport within Arizona and New Mexico." },
      { role: "ai", text: "A mixed fleet of that size with interstate operations has moderate to high auto liability exposure. Let me ask about your driver qualification program — do you conduct Motor Vehicle Record (MVR) checks on all drivers, and how frequently? What is your minimum driver age requirement?" },
    ],
    findings: [
      { sev: "High", title: "MVR Checks Not Conducted Annually", conf: 87, desc: "Driver MVR checks are conducted only at hire. No annual re-check process documented for existing drivers.", rec: "Implement annual MVR review for all drivers. Establish written criteria for acceptable MVR (violations, accidents). Remove drivers who exceed thresholds.", ref: "FMCSA 49 CFR 391.25; carrier driver qualification standards" },
      { sev: "High", title: "No Post-Accident Drug & Alcohol Testing Protocol", conf: 84, desc: "Company confirms no formal post-accident drug testing policy. Three at-fault accidents reported in the past 24 months.", rec: "Adopt written post-accident testing protocol per DOT/FMCSA guidelines. Partner with occupational health provider for testing.", ref: "FMCSA 49 CFR Part 382; carrier auto policy requirement §5.3" },
      { sev: "Medium", title: "Distracted Driving Policy Not in Writing", conf: 76, desc: "Company reports a verbal 'no phone use' policy but no signed acknowledgment from drivers or written documentation.", rec: "Implement written distracted driving policy with annual driver acknowledgment signature. Consider dashcam deployment.", ref: "NTSB and FMCSA distracted driving guidance; carrier loss control bulletin" },
      { sev: "Medium", title: "Vehicle Pre-Trip Inspection Records Incomplete", conf: 72, desc: "Only 9 of 22 vehicles have pre-trip inspection logs on file for the past 30 days. Remaining vehicles have no documented checks.", rec: "Implement daily driver vehicle inspection reports (DVIR) for all vehicles. Supervisor review weekly.", ref: "FMCSA 49 CFR 396.11; carrier fleet maintenance standard" },
      { sev: "Low", title: "Two Vehicles Garaged at Driver Residences", conf: 65, desc: "Box truck #14 and #18 are kept at driver residences overnight. No additional security or telematics controls documented.", rec: "Document authorization for off-site storage. Install GPS telematics on both vehicles.", ref: "Carrier underwriting guidelines §6.2 — physical damage controls" },
    ],
    risk_score_detail: { critical: 0, high: 2, medium: 2, low: 1 },
    project_plan: [
      { action: "Implement annual MVR review process for all 22 drivers", priority: "High", timeframe: "Within 30 days" },
      { action: "Adopt and document post-accident drug & alcohol testing protocol", priority: "High", timeframe: "Within 14 days" },
      { action: "Draft and distribute written distracted driving policy — obtain driver signatures", priority: "Medium", timeframe: "Within 30 days" },
      { action: "Implement daily DVIR forms for all fleet vehicles", priority: "Medium", timeframe: "Within 21 days" },
      { action: "Install GPS telematics on vehicles garaged off-site", priority: "Low", timeframe: "Within 60 days" },
    ],
  },
};

const SEV_STYLES = {
  Critical: { badge: "bg-red-100 text-red-700 border border-red-200", bar: "bg-red-500" },
  High:     { badge: "bg-orange-100 text-orange-700 border border-orange-400/30", bar: "bg-orange-500" },
  Medium:   { badge: "bg-amber-100 text-amber-700 border border-amber-300", bar: "bg-amber-500" },
  Low:      { badge: "bg-green-100 text-green-700 border border-green-200", bar: "bg-green-500" },
};
const RISK_STYLE = {
  Low: "#10B981", Moderate: "#F59E0B", High: "#F97316", Critical: "#EF4444"
};

function SampleFindingCard({ finding, index }) {
  const [open, setOpen] = useState(index < 2);
  const s = SEV_STYLES[finding.sev] || SEV_STYLES.Low;
  return (
    <div className={`border rounded-lg overflow-hidden transition-all duration-200 ${open ? "border-slate-300" : "border-slate-200"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50 text-left transition-colors">
        <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${s.badge}`}>{finding.sev}</span>
        <span className="flex-1 font-medium text-slate-800 text-sm">{finding.title}</span>
        <span className="text-xs text-slate-400 mr-2">{finding.conf}% confidence</span>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-slate-50 border-t border-slate-100 space-y-2.5">
          <p className="text-sm text-slate-700 leading-relaxed">{finding.desc}</p>
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
            <p className="text-xs text-blue-800"><span className="font-semibold">Recommendation:</span> {finding.rec}</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-slate-400" />
            <p className="text-xs text-slate-400 italic">{finding.ref}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SampleOutputSection({ onDemo }) {
  const [activeTab, setActiveTab] = useState("commercial_property");
  const [activeView, setActiveView] = useState("findings");
  const survey = SAMPLE_SURVEYS[activeTab];

  return (
    <section id="sample-output" className="py-24 px-6 bg-slate-50">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <p className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">Sample Survey Output</p>
            <h2 className="font-heading text-4xl font-bold text-slate-800 leading-tight mb-4">
              See exactly what your loss control team receives
            </h2>
            <p className="text-slate-500 text-lg leading-relaxed mb-6">
              Representative outputs based on real-world OSHA regulations, NFPA codes, and carrier underwriting guidelines. Your surveys reflect your specific guidelines.
            </p>
            <div className="space-y-3 mb-8">
              {[
                { icon: MessageSquare, text: "AI-guided chat captures all inspection data" },
                { icon: Camera, text: "Photo analysis with specific hazard annotation" },
                { icon: BarChart2, text: "Risk score with severity-weighted calculation" },
                { icon: FileText, text: "Editable findings reviewed by your loss control team" },
                { icon: Download, text: "Carrier-branded PDF + project plan delivered" },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-3.5 h-3.5 text-navy" />
                  </div>
                  <span className="text-sm text-slate-600">{item.text}</span>
                </div>
              ))}
            </div>
            <button onClick={onDemo} data-testid="sample-request-demo"
              className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white rounded-xl font-semibold text-sm hover:bg-navy-dark transition-colors shadow-md">
              Schedule a Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(SAMPLE_SURVEYS).map(([key, s]) => (
                <button key={key} onClick={() => { setActiveTab(key); setActiveView("findings"); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 ${activeTab === key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy/30"}`}>
                  {s.label}
                </button>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-navy px-5 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Loss Control Survey — {survey.label}</div>
                    <div className="font-heading font-bold text-white text-base">{survey.insured}</div>
                    <div className="text-white/60 text-xs mt-0.5">{survey.address}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-3xl" style={{ color: RISK_STYLE[survey.risk_level] }}>{survey.risk_score}</div>
                    <div className="text-white/50 text-xs">/ 100 Risk Score</div>
                    <div className="font-semibold text-xs mt-0.5" style={{ color: survey.risk_level === "High" ? "#FED7AA" : survey.risk_level === "Moderate" ? "#FDE68A" : "#A7F3D0" }}>
                      {survey.risk_level} Risk
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {[["Critical", survey.risk_score_detail.critical, "#FCA5A5"], ["High", survey.risk_score_detail.high, "#FDBA74"], ["Medium", survey.risk_score_detail.medium, "#FDE68A"], ["Low", survey.risk_score_detail.low, "#A7F3D0"]].map(([lbl, cnt, clr]) => (
                    <div key={lbl} className="bg-white/10 rounded-lg py-2 text-center">
                      <div className="font-heading font-bold text-lg" style={{ color: clr }}>{cnt}</div>
                      <div className="text-white/50 text-xs">{lbl}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex border-b border-slate-200">
                {[["findings", "Findings"], ["chat", "AI Conversation"], ["plan", "Project Plan"]].map(([k, label]) => (
                  <button key={k} onClick={() => setActiveView(k)}
                    className={`flex-1 py-2.5 text-xs font-medium transition-colors duration-150 ${activeView === k ? "text-navy border-b-2 border-navy bg-slate-50" : "text-slate-500 hover:text-slate-700 bg-white"}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-4 max-h-[500px] overflow-y-auto space-y-3">
                {activeView === "findings" && survey.findings.map((f, i) => (
                  <SampleFindingCard key={i} finding={f} index={i} />
                ))}
                {activeView === "chat" && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-400 text-center pb-1">Sample AI survey conversation</p>
                    {survey.chat.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === "ai" ? "justify-start" : "justify-end"}`}>
                        {msg.role === "ai" && (
                          <div className="w-6 h-6 bg-navy rounded-full flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                            <Shield className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <div className={`max-w-xs rounded-xl px-3 py-2.5 text-xs leading-relaxed ${msg.role === "ai" ? "bg-navy text-white" : "bg-slate-100 text-slate-700"}`}>
                          {msg.text.split("---FINDING---")[0]}
                          {msg.text.includes("---FINDING---") && (
                            <div className="mt-2 bg-white/10 rounded-lg p-2 text-white/70 text-xs">
                              <span className="font-bold text-red-300">Finding flagged</span> — AI identified a potential issue
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="text-xs text-slate-400 text-center pt-1">Full conversation continues with photo uploads and analysis...</div>
                  </div>
                )}
                {activeView === "plan" && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 pb-1">Corrective action plan — generated automatically from findings</p>
                    {survey.project_plan.map((item, i) => {
                      const s = SEV_STYLES[item.priority] || SEV_STYLES.Low;
                      return (
                        <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-3">
                          <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold text-slate-500 flex-shrink-0 mt-0.5">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">{item.action}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{item.timeframe}</p>
                          </div>
                          <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${s.badge}`}>{item.priority}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-400">Sample output — based on industry standards</p>
                <div className="flex items-center gap-1.5 text-navy text-xs font-medium">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Human-reviewed before delivery
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Main Landing Page ───────────────────────────────────────────────────────
export default function LandingPage() {
  const [showDemo, setShowDemo] = useState(false);

  return (
    <div className="min-h-screen bg-white font-body">
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}

      {/* ── Nav ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="container mx-auto px-6 max-w-7xl h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-navy text-lg">RiskSurvey<span className="text-slate-400">.ai</span></span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-slate-500 hover:text-navy text-sm font-medium transition-colors">How It Works</a>
            <a href="#sample-output" className="text-slate-500 hover:text-navy text-sm font-medium transition-colors">Sample Output</a>
            <a href="#ai-controls" className="text-slate-500 hover:text-navy text-sm font-medium transition-colors">AI Controls</a>
            <a href="#security" className="text-slate-500 hover:text-navy text-sm font-medium transition-colors">Security</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-500 hover:text-navy transition-colors px-3 py-2" data-testid="nav-signin">Sign In</Link>
            <button onClick={() => setShowDemo(true)} data-testid="nav-schedule-demo"
              className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition-colors shadow-sm">
              Schedule a Demo
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-32 pb-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left copy */}
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 bg-navy/5 border border-navy/10 rounded-full px-4 py-1.5 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-navy/60"></span>
                <span className="text-navy/70 text-xs font-semibold tracking-wide">Purpose-built for Insurance Carriers</span>
              </div>
              <h1 className="font-heading text-5xl lg:text-6xl font-bold text-navy leading-[1.08] tracking-tight mb-6">
                Loss control surveys,<br />
                <span className="text-slate-400">done smarter.</span>
              </h1>
              <p className="text-slate-500 text-xl leading-relaxed mb-10 max-w-lg">
                RiskSurvey.ai guides your insureds through structured loss control surveys via chat, analyzes their photos, and delivers professional PDF reports — trained on your underwriting guidelines.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <button onClick={() => setShowDemo(true)} data-testid="hero-schedule-demo"
                  className="flex items-center gap-2 px-7 py-3.5 bg-navy text-white rounded-xl font-semibold text-sm hover:bg-navy-dark transition-all shadow-lg shadow-navy/20 hover:shadow-navy/30 hover:-translate-y-px">
                  Schedule a Demo <ArrowRight className="w-4 h-4" />
                </button>
                <a href="#sample-output"
                  className="flex items-center gap-2 px-7 py-3.5 border border-slate-200 text-slate-600 rounded-xl font-medium text-sm hover:border-navy/30 hover:text-navy transition-colors">
                  See a Sample Report <ChevronDown className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right: product preview card */}
            <div className="lg:col-span-6">
              <div className="relative">
                {/* Soft background glow */}
                <div className="absolute -inset-4 bg-gradient-to-br from-navy/5 to-transparent rounded-3xl" />
                <div className="relative bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/60 overflow-hidden">
                  {/* Card header */}
                  <div className="bg-navy px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-0.5">AI Survey — Commercial Property</p>
                      <p className="font-heading font-bold text-white text-sm">Riverside Industrial Park, Unit 4</p>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-bold text-2xl text-orange-300">72</div>
                      <div className="text-white/40 text-xs">Risk Score</div>
                    </div>
                  </div>
                  {/* AI chat preview */}
                  <div className="p-4 space-y-3 bg-slate-50/50">
                    <div className="flex items-start gap-2.5">
                      <div className="w-6 h-6 bg-navy rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="w-3 h-3 text-white" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl rounded-tl-sm px-3 py-2.5 text-xs text-slate-700 leading-relaxed max-w-xs shadow-sm">
                        Please upload a photo of your main electrical panel and any visible subpanels.
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-navy/90 text-white rounded-xl rounded-tr-sm px-3 py-2.5 text-xs leading-relaxed max-w-xs">
                        Here&apos;s a photo of the panel.
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <div className="w-6 h-6 bg-navy rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="w-3 h-3 text-white" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl rounded-tl-sm px-3 py-2.5 text-xs text-slate-700 leading-relaxed max-w-xs shadow-sm">
                        I can see double-tapped breakers on circuits 4, 7, and 12.
                        <div className="mt-2 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">
                          <span className="text-xs font-bold text-red-600">Critical finding flagged</span>
                          <span className="text-red-500 text-xs"> — 94% confidence</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Findings preview */}
                  <div className="border-t border-slate-200 p-4 space-y-2">
                    {[
                      { sev: "Critical", label: "bg-red-100 text-red-700", text: "Double-Tapped Electrical Breakers", conf: "94%" },
                      { sev: "Critical", label: "bg-red-100 text-red-700", text: "Sprinkler System — No Annual Inspection", conf: "91%" },
                      { sev: "High", label: "bg-orange-100 text-orange-700", text: "Roof Membrane Deterioration", conf: "85%" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.label}`}>{item.sev}</span>
                        <span className="text-xs text-slate-700 flex-1">{item.text}</span>
                        <span className="text-xs text-slate-400">{item.conf}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-navy" />
                    <span className="text-xs text-navy font-medium">Awaiting loss control review before delivery</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem / Value strip ── */}
      <section className="py-16 px-6 border-y border-slate-100 bg-slate-50">
        <div className="container mx-auto max-w-5xl">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-10">The business case for a smarter approach</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: TrendingDown, val: "85%", label: "Lower cost per survey", sub: "Traditional field inspections cost $350–$500 each. RiskSurvey.ai delivers the same quality for under $65." },
              { icon: Clock, val: "Hours", label: "Not weeks", sub: "Insureds complete surveys on their own schedule. Reports reach your team the same day — not weeks later." },
              { icon: Award, val: "100%", label: "Your brand, your guidelines", sub: "Every survey is trained on your underwriting guidelines and delivered under your carrier identity." },
            ].map((item) => (
              <div key={item.val} className="text-center px-4">
                <div className="w-10 h-10 bg-navy/8 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-5 h-5 text-navy" />
                </div>
                <div className="font-heading font-bold text-4xl text-navy mb-1">{item.val}</div>
                <div className="font-semibold text-slate-700 text-sm mb-2">{item.label}</div>
                <p className="text-slate-500 text-sm leading-relaxed">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Survey Types ── */}
      <section className="py-10 px-6 border-b border-slate-100 bg-white">
        <div className="container mx-auto max-w-7xl px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 text-center mb-5">Supports All Major Lines of Business</p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            {[
              { icon: Building2, label: "Commercial Property" },
              { icon: HardHat, label: "Workers' Compensation" },
              { icon: Home, label: "Habitational" },
              { icon: Shield, label: "General Liability" },
              { icon: Truck, label: "Commercial Auto" },
            ].map(type => (
              <div key={type.label} className="flex items-center gap-2 text-slate-500">
                <type.icon className="w-4 h-4 text-navy/60" />
                <span className="text-sm font-medium">{type.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">The Process</p>
            <h2 className="font-heading text-4xl font-bold text-slate-800 leading-tight mb-4">From order to delivered report in hours</h2>
            <p className="text-slate-500 text-lg">Four steps. No field visits required.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "01", icon: ClipboardList, title: "Carrier Onboards", desc: "Your loss control team uploads underwriting guidelines and configures survey types for each line of business." },
              { step: "02", icon: Zap,           title: "Order a Survey",   desc: "Underwriter creates a survey order — manually or via CSV — and a secure, time-limited insured link is generated." },
              { step: "03", icon: MessageSquare,  title: "Insured Completes", desc: "Insured receives the link, chats with the AI survey assistant, and uploads photos from any device — no app needed." },
              { step: "04", icon: FileText,       title: "Review & Deliver", desc: "Your team reviews AI findings, edits as needed, approves, and downloads or emails the final branded PDF report." },
            ].map((s, i) => (
              <div key={s.step} className="relative group">
                <div className="bg-white border border-slate-200 rounded-xl p-6 h-full hover:border-navy/20 hover:shadow-md transition-all duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-mono text-xs font-bold text-slate-300">{s.step}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-slate-800 mb-2">{s.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
                {i < 3 && <div className="hidden md:block absolute top-9 -right-3 w-6 h-px bg-slate-200 z-10" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sample Output ── */}
      <SampleOutputSection onDemo={() => setShowDemo(true)} />

      {/* ── AI Controls ── */}
      <section id="ai-controls" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">Human-in-the-Loop</p>
              <h2 className="font-heading text-4xl font-bold text-slate-800 leading-tight mb-4">
                The AI assists. Your team decides.
              </h2>
              <p className="text-slate-500 text-lg leading-relaxed mb-6">
                Every finding is transparent, editable, and requires human review before it reaches the insured. No black boxes. No surprises.
              </p>
              <div className="bg-navy/5 border border-navy/10 rounded-xl p-4">
                <p className="text-sm text-navy font-medium">Critical and High severity findings require explicit underwriter sign-off before any report can be finalized or delivered.</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { num: "01", title: "Confidence Scoring", desc: "Every AI finding displays a confidence percentage so reviewers know where to focus their attention." },
                { num: "02", title: "Photo Annotation", desc: "AI identifies the specific element in each photo that triggered a concern — not just the image as a whole." },
                { num: "03", title: "Reasoning Transparency", desc: "Each finding cites the specific guideline, NFPA code, or OSHA standard that makes it a risk concern." },
                { num: "04", title: "Mandatory Review Gate", desc: "Critical and High findings require human sign-off before any report can be finalized." },
              ].map((c) => (
                <div key={c.num} className="flex gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-navy/20 hover:shadow-sm transition-all duration-200">
                  <div className="font-mono text-sm font-bold text-navy/30 flex-shrink-0 w-7 mt-0.5">{c.num}</div>
                  <div>
                    <h4 className="font-heading font-semibold text-slate-800 mb-1">{c.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Security & Trust ── */}
      <section id="security" className="py-24 px-6 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">Security & Trust</p>
            <h2 className="font-heading text-4xl font-bold text-slate-800 leading-tight mb-4">
              Built with enterprise-grade security from day one
            </h2>
            <p className="text-slate-500 text-lg leading-relaxed">
              Carrier data is sensitive. RiskSurvey.ai is designed with strict data isolation, full audit trails, and the controls your IT team will ask about.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Users,    title: "Complete Carrier Isolation",      desc: "Multi-tenant architecture ensures each carrier's data, users, inspections, and reports are completely separated. Carriers never see each other's data." },
              { icon: Lock,     title: "Role-Based Access Control",       desc: "Platform admins, underwriters, and loss control staff have separate permission levels. Insureds access only their specific survey via a time-limited link." },
              { icon: Eye,      title: "Full Audit Logging",              desc: "Every user action — logins, report edits, finding approvals, PDF downloads — is logged with timestamp and user identity for compliance and review." },
              { icon: Clock,    title: "Survey Link Expiration",          desc: "Insured survey links expire automatically after 30 days. Expired links return an access-denied response — no stale survey access." },
              { icon: Shield,   title: "Mandatory Human Review Gate",     desc: "No Critical or High severity finding can be included in a final delivered report without explicit underwriter approval. The AI never acts autonomously." },
              { icon: Award,    title: "SOC 2 Type I Roadmap",            desc: "RiskSurvey.ai is building toward SOC 2 Type I certification. Data is encrypted in transit (TLS 1.3) and we maintain a formal vendor risk program." },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-navy/20 hover:shadow-sm transition-all duration-200">
                <div className="w-10 h-10 bg-navy/8 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-navy" />
                </div>
                <h3 className="font-heading font-semibold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── White-Label ── */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-navy mb-3">Your Brand</p>
          <h2 className="font-heading text-4xl font-bold text-slate-800 leading-tight mb-4">
            Insureds never see our name
          </h2>
          <p className="text-slate-500 text-xl leading-relaxed max-w-2xl mx-auto mb-12">
            RiskSurvey.ai is fully white-labeled. Your logo, your colors, your domain. The survey experience, the PDF report, and every insured touchpoint carries your carrier identity — not ours.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { title: "Branded Insured Portal", desc: "The survey link and chat interface display your carrier logo and name. Insureds interact with your brand throughout." },
              { title: "Your Logo on Every Report", desc: "Generated PDF reports include your carrier logo, letterhead, and contact information on every page." },
              { title: "Trained on Your Guidelines", desc: "The AI is calibrated to your specific underwriting guidelines, not generic industry defaults. Your standards, applied consistently." },
            ].map((item) => (
              <div key={item.title} className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                <div className="w-2 h-2 rounded-full bg-navy mb-4"></div>
                <h3 className="font-heading font-semibold text-slate-800 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 bg-navy">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-4 leading-tight">
            Ready to modernize your loss control program?
          </h2>
          <p className="text-white/55 text-lg mb-10 leading-relaxed">
            RiskSurvey.ai is configured for each carrier — white-labeled, trained on your guidelines, and ready to deploy. Let us show you a live demo with real survey data.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button onClick={() => setShowDemo(true)} data-testid="cta-schedule-demo"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-navy rounded-xl font-bold hover:bg-slate-100 transition-colors shadow-lg text-base">
              Schedule a 30-min Demo <ArrowRight className="w-5 h-5" />
            </button>
            <a href="mailto:bill.raab@fractionalriskgroup.com"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white/80 rounded-xl font-medium hover:bg-white/10 transition-colors text-base">
              <Mail className="w-5 h-5" /> Email Us Directly
            </a>
          </div>
          <p className="text-white/25 text-sm mt-8">Implementation includes carrier onboarding, guideline ingestion, and team training.</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-slate-900 px-6 py-8">
        <div className="container mx-auto max-w-7xl flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 bg-white/10 rounded flex items-center justify-center">
              <Shield className="w-3 h-3 text-white/60" />
            </div>
            <span className="font-heading font-bold text-white/50 text-sm">RiskSurvey.ai</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/terms" className="text-slate-500 hover:text-slate-300 text-xs transition-colors duration-150">Terms of Service</a>
            <a href="/trust" data-testid="footer-trust-link" className="text-slate-500 hover:text-slate-300 text-xs transition-colors duration-150">Trust & Security</a>
            <a href="/privacy" className="text-slate-500 hover:text-slate-300 text-xs transition-colors duration-150">Privacy Policy</a>
            <p className="text-slate-500 text-xs">© 2026 RiskSurvey.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
